import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Form, Input, Select, Switch } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import Modal from 'antd/lib/modal/Modal';

import { useAppDispatch, useAppSelector } from '@/Hooks/useAppRedux';

import { getMallCategories } from '@/Redux/slices/PostManagementSlice';
import { createChannel } from '@/Redux/slices/VodSlice';

interface ModalProps {
  isModalVisible: boolean;
  setModalVisible: (visible: boolean) => void;
}

const CreateChannelModal = ({ isModalVisible, setModalVisible }: ModalProps) => {
  const dispatch = useAppDispatch();
  const { mallCategories } = useAppSelector((state) => state.postManagement.value);
  const userName = useAppSelector((state) => state.account.user.username);
  const { channels, channelVersion } = useAppSelector((state) => state.vod.value);

  const [form] = useForm();
  const formValues = Form.useWatch([], form);
  const [submittable, setSubmittable] = useState(false);

  useEffect(() => {
    if (userName) {
      dispatch(getMallCategories());
    }
  }, [dispatch, userName]);

  useEffect(() => {
    form
      .validateFields()
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [form, formValues]);

  const mallCategoryChannelCount = useMemo(() => {
    const counts: { [key: string]: number } = {};

    channels.forEach((channel) => {
      if (channel.mallCategoryCode) {
        counts[channel.mallCategoryCode] = (counts[channel.mallCategoryCode] || 0) + 1;
      }
    });

    return counts;
  }, [channels]);

  const mallCategorySelectOptions = useMemo(
    () =>
      (mallCategories || []).map((c) => ({
        label: c.name,
        value: c.categoryId,
        disabled: (mallCategoryChannelCount[c.categoryId] ?? 0) >= 50,
      })),
    [mallCategories, mallCategoryChannelCount],
  );

  const onChannelCreate = () => {
    const payload = {
      channelName: formValues.channel,
      disabled: formValues.disabled,
      mallCategoryCode: formValues.mallCategoryCode,
      remark: formValues.remark || '',
    };

    const data = {
      data: payload,
      version: channelVersion,
    };

    form.resetFields();
    setModalVisible(false);
    dispatch(createChannel(data));
  };

  const onModalClose = useCallback(() => {
    form.resetFields();
    setModalVisible(false);
  }, [form, setModalVisible]);

  return (
    <Modal
      title="Create Channel"
      open={isModalVisible}
      closable
      onCancel={onModalClose}
      okText="Create"
      onOk={onChannelCreate}
      okButtonProps={{ htmlType: 'submit', disabled: !submittable }}
    >
      <Form form={form} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
        <Form.Item
          name="channel"
          label="Channel"
          rules={[
            {
              min: 1,
              required: true,
              message: 'Channel name cannot be empty',
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="disabled" label="Disabled" valuePropName="checked" initialValue>
          <Switch />
        </Form.Item>
        <Form.Item
          name="mallCategoryCode"
          label="Mall Category"
          rules={[{ required: true, message: 'Please select.' }]}
        >
          <Select placeholder="Mall Category" options={mallCategorySelectOptions} />
        </Form.Item>
        <Form.Item name="remark" label="Remark">
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateChannelModal;
